import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, asApiError } from '../lib/api';
import type { SchedulerRow, ScheduleStatus } from '../types';
import { DpasBadge } from '../components/DpasBadge';
import { HealthStoplight } from '../components/HealthStoplight';
import { CommsModal } from '../components/CommsModal';
import { CuiLabel } from '../components/CuiLabel';
import styles from './Queue.module.css';

const QUEUE_KEY = ['scheduler', 'queue'];
const STATUSES: ScheduleStatus[] = ['UNSCHEDULED', 'SCHEDULED', 'AT_RISK', 'HOLD', 'SHIPPED'];

export function SchedulerQueue() {
    const queue = useQuery({ queryKey: QUEUE_KEY, queryFn: api.schedulerQueue });
    const [commsFor, setCommsFor] = useState<SchedulerRow | null>(null);

    if (queue.isLoading) return <p className={styles.state}>Loading queue…</p>;
    if (queue.isError) return <p className={styles.error}>Failed to load the scheduler queue.</p>;
    const rows = queue.data?.rows ?? [];

    return (
        <div className={styles.screen}>
            <h2 className={styles.heading}>
                Schedule Movement queue{' '}
                <span className={styles.count}>{rows.length} in process</span>
            </h2>
            <div className={styles.tableWrap}>
                <table>
                    <thead>
                        <tr>
                            <th>Health</th>
                            <th>PO</th>
                            <th>Customer</th>
                            <th>DPAS</th>
                            <th>Need date</th>
                            <th>Promised date</th>
                            <th>Slip</th>
                            <th>Lead-time driver</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <Row key={row.line_id} row={row} onComms={() => setCommsFor(row)} />
                        ))}
                    </tbody>
                </table>
            </div>
            {commsFor && (
                <CommsModal
                    lineId={commsFor.line_id}
                    title={commsFor.po_number}
                    onClose={() => setCommsFor(null)}
                />
            )}
        </div>
    );
}

function Row({ row, onComms }: { row: SchedulerRow; onComms: () => void }) {
    const qc = useQueryClient();
    const [promised, setPromised] = useState(row.current_promised_date ?? '');
    const [overrideFor, setOverrideFor] = useState<string | null>(null); // promised date awaiting justification
    const [error, setError] = useState<string | null>(null);
    const [ltdNote, setLtdNote] = useState<string | null>(null);

    const invalidate = () => qc.invalidateQueries({ queryKey: QUEUE_KEY });
    const onError = (e: unknown) => setError(asApiError(e)?.body?.error ?? 'Action failed');

    const setDate = useMutation({
        mutationFn: (vars: { date: string; justification?: string }) =>
            api.promisedDate(row.line_id, vars.date, vars.justification),
        onSuccess: () => {
            setError(null);
            setOverrideFor(null);
            invalidate();
        },
        onError: (e: unknown) => {
            const apiErr = asApiError(e);
            if (apiErr?.status === 409 && apiErr.body?.code === 'DPAS_OVERRIDE_REQUIRED') {
                setOverrideFor(promised); // open the justification modal for this date
            } else {
                onError(e);
            }
        },
    });
    const expedite = useMutation({
        mutationFn: () => api.expedite(row.line_id, !row.customer_expedite_flag),
        onSuccess: invalidate,
        onError,
    });
    const status = useMutation({
        mutationFn: (s: ScheduleStatus) => api.setStatus(row.line_id, s),
        onSuccess: invalidate,
        onError,
    });
    const suggest = useMutation({
        mutationFn: async () => {
            const { suggestion, reason } = await api.suggestedLtd(row.line_id);
            if (!suggestion) {
                setLtdNote(reason ?? 'No suggestion — enter manually');
                return;
            }
            await api.setLeadTimeDriver(row.line_id, suggestion.desc, suggestion.type);
            setLtdNote(null);
        },
        onSuccess: invalidate,
        onError,
    });

    const busy = setDate.isPending || expedite.isPending || status.isPending || suggest.isPending;

    return (
        <>
            <tr>
                <td>
                    <HealthStoplight health={row.health} />
                </td>
                <td>{row.po_number}</td>
                <td>{row.customer_name ?? '—'}</td>
                <td>
                    <DpasBadge rating={row.dpas_rating} />
                </td>
                <td className={row.real_need_date ? undefined : styles.muted}>
                    {row.real_need_date ?? 'not set'}
                </td>
                <td>
                    <span className={styles.inlineEdit}>
                        <input
                            type="date"
                            aria-label={`Promised date for ${row.po_number}`}
                            value={promised}
                            onChange={(e) => setPromised(e.target.value)}
                        />
                        <button
                            disabled={busy || !promised}
                            onClick={() => setDate.mutate({ date: promised })}
                        >
                            Set
                        </button>
                    </span>
                </td>
                <td className={(row.slip_days ?? 0) > 0 ? styles.expedite : undefined}>
                    {row.slip_days == null ? '—' : `${row.slip_days}d`}
                </td>
                <td className={styles.ltd}>
                    {row.lead_time_driver_desc ? (
                        <span>
                            {row.lead_time_driver_desc}{' '}
                            <span className={styles.muted}>({row.lead_time_driver_source})</span>
                        </span>
                    ) : (
                        <span className={styles.ltdManual}>none</span>
                    )}
                    <br />
                    <button disabled={busy} onClick={() => suggest.mutate()}>
                        Auto-suggest
                    </button>
                    {ltdNote && <span className={styles.rowError}>{ltdNote}</span>}
                </td>
                <td>
                    <select
                        aria-label={`Schedule status for ${row.po_number}`}
                        value={row.schedule_status}
                        disabled={busy}
                        onChange={(e) => status.mutate(e.target.value as ScheduleStatus)}
                    >
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </td>
                <td>
                    <span className={styles.actions}>
                        <button onClick={onComms}>Comms</button>
                        <button
                            className={row.customer_expedite_flag ? 'primary' : undefined}
                            disabled={busy}
                            onClick={() => expedite.mutate()}
                        >
                            {row.customer_expedite_flag ? 'Expediting' : 'Expedite'}
                        </button>
                    </span>
                    {error && <span className={styles.rowError}>{error}</span>}
                </td>
            </tr>
            {overrideFor && (
                <DpasOverrideModal
                    poNumber={row.po_number}
                    dpas={row.dpas_rating ?? 'NONE'}
                    contractDate={row.contract_date}
                    promisedDate={overrideFor}
                    busy={setDate.isPending}
                    onCancel={() => setOverrideFor(null)}
                    onConfirm={(justification) =>
                        setDate.mutate({ date: overrideFor, justification })
                    }
                />
            )}
        </>
    );
}

function DpasOverrideModal({
    poNumber,
    dpas,
    contractDate,
    promisedDate,
    busy,
    onCancel,
    onConfirm,
}: {
    poNumber: string;
    dpas: string;
    contractDate: string;
    promisedDate: string;
    busy: boolean;
    onCancel: () => void;
    onConfirm: (justification: string) => void;
}) {
    const [text, setText] = useState('');
    return (
        <tr>
            <td colSpan={10}>
                <div role="dialog" aria-modal="true" aria-label={`DPAS override — ${poNumber}`}>
                    <strong>⚠ DPAS override required</strong> — {poNumber} is {dpas}-rated and the
                    new promised date ({promisedDate}) slips past the contract date ({contractDate}
                    ). This override is logged permanently.
                    <CuiLabel />
                    <div className={styles.inlineEdit}>
                        <input
                            aria-label="Override justification"
                            placeholder="Justification (required)"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                        />
                        <button
                            className="primary"
                            disabled={busy || !text.trim()}
                            onClick={() => onConfirm(text.trim())}
                        >
                            Confirm override
                        </button>
                        <button disabled={busy} onClick={onCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
            </td>
        </tr>
    );
}
